import { Component, Input } from '@builder.io/sdk';

import sectionFields from 'molecules/Section/config';

const fontSizeOptions = [
  { label: 'XS', value: 'text-xs' },
  { label: 'SM', value: 'text-sm' },
  { label: 'MD', value: 'text-md' },
  { label: 'LG', value: 'text-lg' },
  { label: 'XL', value: 'text-xl' },
] as const;

const fontWeightOptions = [
  { label: 'Medium', value: 'font-medium' },
  { label: 'Semibold', value: 'font-semibold' },
  { label: 'Bold', value: 'font-bold' },
] as const;

const contentTableColumnFields = [
  {
    name: 'id',
    type: 'string',
    required: true,
    showIf: 'false',
    helperText: 'Auto-generated ID. Hidden from the editor.',
  },
  {
    name: 'label',
    type: 'string',
    defaultValue: 'Column',
    helperText: 'Column header text displayed in the top row.',
  },
] as const satisfies DeepReadonly<Input[]>;

const contentTableCellFields = [
  {
    name: 'text',
    type: 'longText',
    defaultValue: '',
    helperText: 'Cell content text.',
  },
] as const satisfies DeepReadonly<Input[]>;

const contentTableRowFields = [
  {
    name: 'id',
    type: 'string',
    required: true,
    showIf: 'false',
    helperText: 'Auto-generated ID. Hidden from the editor.',
  },
  {
    name: 'rowHeader',
    type: 'string',
    defaultValue: '',
    helperText: 'Row label shown in the left column. Leave empty for no row label.',
  },
  {
    name: 'cells',
    type: 'list',
    subFields: contentTableCellFields,
    defaultValue: [{ text: '' }, { text: '' }],
    helperText: 'Cell values for this row. Add one cell per column.',
  },
] as const satisfies DeepReadonly<Input[]>;

const contentTableFields = [
  // — Heading —
  {
    name: 'heading',
    type: 'string',
    helperText: 'Optional heading shown above the table. Leave empty for no heading.',
  },
  {
    name: 'headingTag',
    type: 'string',
    enum: [
      { label: 'H2', value: 'h2' },
      { label: 'H3', value: 'h3' },
      { label: 'H4', value: 'h4' },
      { label: 'H5', value: 'h5' },
    ],
    defaultValue: 'h3',
    showIf: "options.get('heading')",
    helperText: 'HTML heading level.',
  },
  {
    name: 'headingAlignment',
    type: 'string',
    enum: [
      { label: 'Left', value: 'left' },
      { label: 'Center', value: 'center' },
    ],
    defaultValue: 'left',
    showIf: "options.get('heading')",
    helperText: 'Heading alignment.',
  },
  // — Table data —
  {
    name: 'columnHeaders',
    type: 'list',
    subFields: contentTableColumnFields,
    defaultValue: [{ label: 'Column 1' }, { label: 'Column 2' }],
    helperText: 'Column headers for the top row. Add one per column.',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (options: Map<string, any>) => {
      if (options.get('columnHeaders')?.length > 0) {
        options.get('columnHeaders').forEach((item: Map<string, string>, index: number) => {
          item.set('id', `col-${index}`);
        });
      }
    },
  },
  {
    name: 'rows',
    type: 'list',
    subFields: contentTableRowFields,
    defaultValue: [
      { rowHeader: 'Row 1', cells: [{ text: '' }, { text: '' }] },
      { rowHeader: 'Row 2', cells: [{ text: '' }, { text: '' }] },
    ],
    helperText: 'Table rows. Add one cell per column in the correct order.',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (options: Map<string, any>) => {
      if (options.get('rows')?.length > 0) {
        options.get('rows').forEach((item: Map<string, string>, index: number) => {
          item.set('id', `row-${index}`);
        });
      }
    },
  },
  // — Column headers styling —
  {
    name: 'hasColumnHeaders',
    type: 'boolean',
    defaultValue: true,
    helperText: 'Show the top row as column headers.',
  },
  {
    name: 'columnHeaderBgColor',
    type: 'color',
    defaultValue: '#f6f6ff',
    showIf: "options.get('hasColumnHeaders') === true",
    helperText: 'Background color for the column header row.',
  },
  {
    name: 'columnHeaderFontSize',
    type: 'string',
    defaultValue: 'text-sm',
    enum: fontSizeOptions,
    showIf: "options.get('hasColumnHeaders') === true",
    helperText: 'Font size for column header text.',
  },
  {
    name: 'columnHeaderFontWeight',
    type: 'string',
    defaultValue: 'font-bold',
    enum: fontWeightOptions,
    showIf: "options.get('hasColumnHeaders') === true",
    helperText: 'Font weight for column header text.',
  },
  // — Row headers styling —
  {
    name: 'hasRowHeaders',
    type: 'boolean',
    defaultValue: true,
    helperText: 'Show the first column as row headers.',
  },
  {
    name: 'rowHeaderBgColor',
    type: 'color',
    defaultValue: '#fafafe',
    showIf: "options.get('hasRowHeaders') === true",
    helperText: 'Background color for row header cells.',
  },
  {
    name: 'rowHeaderFontSize',
    type: 'string',
    defaultValue: 'text-sm',
    enum: fontSizeOptions,
    showIf: "options.get('hasRowHeaders') === true",
    helperText: 'Font size for row header text.',
  },
  {
    name: 'rowHeaderFontWeight',
    type: 'string',
    defaultValue: 'font-semibold',
    enum: fontWeightOptions,
    showIf: "options.get('hasRowHeaders') === true",
    helperText: 'Font weight for row header text.',
  },
  // — Cell styling —
  {
    name: 'cellBgColor',
    type: 'color',
    defaultValue: '#ffffff',
    helperText: 'Background color for regular cells.',
  },
  {
    name: 'cellFontSize',
    type: 'string',
    defaultValue: 'text-sm',
    enum: fontSizeOptions,
    helperText: 'Font size for regular cell text.',
  },
  {
    name: 'cellFontWeight',
    type: 'string',
    defaultValue: 'font-medium',
    enum: fontWeightOptions,
    helperText: 'Font weight for regular cell text.',
  },
  ...sectionFields,
] as const satisfies DeepReadonly<Input[]>;

const contentTableConfig = {
  name: 'contentTable',
  inputs: contentTableFields,
} as Component;

export type ContentTableColumnField = GenerateBuilder<typeof contentTableColumnFields>;
export type ContentTableCellField = GenerateBuilder<typeof contentTableCellFields>;
export type ContentTableRowField = GenerateBuilder<typeof contentTableRowFields>;
export type ContentTableConfigProps = GenerateBuilder<typeof contentTableFields>;

export default contentTableConfig;
